import { type HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { useState, useEffect, useRef } from 'react'

import { BrowserLocalstorage } from '@/app/store'

// --- Типы данных ---
interface NotificationInfo {
    PartName: string
    Percent: number
    InsuraceCompany: string
    InsuraceContract: string
    ImporterLogin: string
}

interface SignalRNotification {
    Key: string
    Info: NotificationInfo | null
    IsError: boolean
    SystemErrorMessage: string | null
}

// --- Хук ---
export const useSignalRNotifications = ({
    onGetErrors,
    onSuccess
}: {
    onGetErrors: (key: string) => void
    onSuccess: () => void
}) => {
    const [notifications, setNotifications] = useState<SignalRNotification[]>([])
    const [isConnected, setIsConnected] = useState(false)
    const connectionRef = useRef<HubConnection | null>(null)

    useEffect(() => {
        const createConnection = async () => {
            try {
                const token = BrowserLocalstorage.getToken() || ''
                const url = process.env.SIGNALR || ''

                const newConnection = new HubConnectionBuilder()
                    .withUrl(url, {
                        accessTokenFactory: () => token,
                        headers: { Authorization: token }
                    })
                    .configureLogging(LogLevel.Information)
                    .build()

                newConnection.on('ReceiveMessage', (message: string | SignalRNotification) => {
                    if (typeof message === 'string' && message.startsWith('System.')) {
                        console.log('System message ignored:', message)
                        return
                    }

                    if (typeof message === 'object' && message !== null && 'Key' in message && 'Info' in message) {
                        setNotifications((prev) => [...prev, message as SignalRNotification])
                        return
                    }

                    if (typeof message === 'string') {
                        try {
                            const parsed = JSON.parse(message) as SignalRNotification
                            if (parsed.Info?.PartName === 'Finish') {
                                onSuccess()
                            }
                            if (parsed.IsError && parsed.Key) {
                                onGetErrors(parsed.Key)
                            }
                            if (parsed?.Key) {
                                if (parsed.Info === null) {
                                    console.warn('Notification has null Info field, but will be added:', parsed)
                                }
                                setNotifications((prev) => [...prev, parsed])
                            } else {
                                console.warn('Parsed JSON missing required field "Key":', parsed)
                            }
                        } catch (error) {
                            console.warn('Non-JSON non-system message:', message)
                        }
                    }
                })

                newConnection.onclose(() => {
                    console.log('SignalR connection closed')
                    setIsConnected(false)
                })
                newConnection.onreconnecting(() => {
                    console.log('SignalR reconnecting...')
                    setIsConnected(false)
                })
                newConnection.onreconnected(() => {
                    console.log('SignalR reconnected')
                    setIsConnected(true)
                })

                await newConnection.start()
                console.log('SignalR connected successfully, id:', newConnection.connectionId)
                connectionRef.current = newConnection
                setIsConnected(true)
            } catch (error) {
                console.error('SignalR connection failed:', error)
            }
        }

        createConnection()

        return () => {
            connectionRef.current?.stop().catch(console.error)
        }
    }, [onGetErrors])

    const sendMessage = async (messageText: string) => {
        if (!connectionRef.current) {
            console.warn('Connection not ready')
            return
        }
        try {
            await connectionRef.current.invoke('SendMessage', messageText)
            console.log('Message sent:', messageText)
        } catch (error) {
            console.error('Error sending message:', error)
        }
    }

    const clearNotifications = () => {
        setNotifications([])
        console.log('signalr disconnectied')
    }

    return {
        notifications, // теперь массив объектов SignalRNotification
        isConnected,
        sendMessage,
        clearNotifications,
        connection: connectionRef.current
    }
}
